import { InputGroupAddon } from "@src/components/ui/input-group";
import { usePromptInputAttachments } from "@src/components/ai-elements/prompt-input-context/usePromptInputAttachments";

import { AttachmentTile } from "./AttachmentTile";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";

/**
 * Composer-side rendering of attached files. Uses the same `AttachmentTile`
 * the chat history renders for visual parity — same square shape, same
 * content-aware previews, same 3-per-row grid sizing. Differences vs
 * history: tiles carry a remove button, show upload status overlays, and
 * tiles aren't link-out (clicking the body doesn't open the URL).
 *
 * Reads its source-of-truth list from `PromptInputProvider` via context, so
 * additions from drag-drop / file-picker / paste all flow through here.
 * Upload status comes from `useFileUpload` (passed in via props) since
 * that's where the s3-upload lifecycle is tracked.
 */
export const AttachmentPreview = () => {
  const { fileUpload } = useExecutionPanelContext();
  const attachments = usePromptInputAttachments();

  if (attachments.files.length === 0) {
    return null;
  }

  const entryById = new Map(fileUpload?.entries.map((e) => [e.id, e]));

  return (
    <InputGroupAddon align="block-start" aria-live="polite">
      <div className="flex w-full flex-wrap gap-2 py-1">
        {attachments.files.map((file) => {
          const entry = entryById.get(file.id);
          // Default to "uploading" on the very first render after the file
          // was added — `useFileUpload`'s effect runs after commit, so the
          // entry doesn't exist on render N. Without this, the tile would
          // render briefly with no overlay before the animation kicks in.
          const status = entry?.status ?? "uploading";
          return (
            <div key={file.id} className="w-[calc((100%-1rem)/3)]">
              <AttachmentTile
                errorMessage={entry?.error}
                filename={file.filename ?? "(unnamed)"}
                mediaType={file.mediaType ?? "application/octet-stream"}
                onRemove={() => attachments.remove(file.id)}
                status={status}
                url={file.url}
              />
            </div>
          );
        })}
      </div>
    </InputGroupAddon>
  );
};
